import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setUserDetails,
  setQuizId,
  setOrganizationData,
  setOrgLogo,
  setStudentTournamentData,
  setTheme,
  setGlobalState,
} from "@src/app/globalSlice";
import { RootState } from "@src/app/store";
import Modal from "@src/components/modal/Modal";
import CommonLoader from "@src/components/loader/CommonLoader";
import QuizLoadError from "@src/components/quiz-load-error/QuizLoadError";
import { USER_CONSTANTS } from "@src/constants/UiConstants";
import { useMulti_rolesMutation } from "@src/api/LoginApiSlice";
import { useQuizUrlQuery } from "@src/api/QuizApiSlice";
import { useUpdateStudentCurrentOrgMutation } from "@src/api/UserApiSlice";
import { toast, Toastify } from "top-brains-ui";
import {
  CurrentQuizState,
  setComponentType,
  setCurrentQuiz,
  setQuizType,
} from "@src/app/currentQuizSlice";
import { isNetworkOrServerError } from "@src/utils/functionUtils";

const JoinQuizHandler = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const accessToken = localStorage.getItem("accessToken");
  const isAuthenticated = !!accessToken;

  const userDetails = useSelector(
    (state: RootState) => state.globalSlice.userDetails,
  );

  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [multiRolesUpdate] = useMulti_rolesMutation();
  const [updateStudentOrg] = useUpdateStudentCurrentOrgMutation();
  const [loading, setLoading] = useState(true);

  const hasRunRef = useRef(false);
  const quizStoredRef = useRef(false);

  const {
    data: quizData,
    error: quizError,
    isFetching,
    refetch,
  } = useQuizUrlQuery(
    { quizId: quizId || "" },
    { skip: !quizId || !isAuthenticated },
  );

  useEffect(() => {
    if (quizError && !isNetworkOrServerError(quizError)) {
      const err = quizError as {
        data?: { response?: string; message?: string };
        message?: string;
      };
      const errorMsg =
        err?.data?.response ||
        err?.data?.message ||
        err?.message ||
        "Failed to join quiz.";
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
      setLoading(false);
    }
  }, [quizError]);

  useEffect(() => {
    if (quizData?.response && !quizStoredRef.current) {
      dispatch(setCurrentQuiz(quizData?.response as CurrentQuizState));
      dispatch(
        setGlobalState({
          selectedOrganizationName: quizData?.response?.organizationName || "",
        }),
      );
      dispatch(setStudentTournamentData(null));
      dispatch(setQuizType("quiz"));
      dispatch(
        setComponentType(
          quizData?.response?.scope === "GLOBAL" ? "website-quiz" : "quiz",
        ),
      );
      dispatch(
        setTheme(
          quizData?.response?.scope === "GLOBAL"
            ? "website-theme"
            : "app-theme",
        ),
      );
    }
  }, [quizData, dispatch]);

  useEffect(() => {
    if (!quizId) return;

    dispatch(setQuizId(quizId));

    if (!isAuthenticated) {
      navigate("/login", {
        state: { from: `/quiz/assign/${quizId}`, showJoinSuccess: true },
        replace: true,
      });
      return;
    }

    if (!userDetails?.id || !userDetails?.privileges) {
      const timer = setTimeout(() => {
        if (!userDetails?.id) {
          toast.error("User details not loaded. Please try again.");
          navigate("/");
        }
      }, 5000);
      return () => clearTimeout(timer);
    }

    const handleJoinQuizFlow = async () => {
      const { privileges, currentPrivilege, ...rest } = userDetails;

      if (
        privileges.includes(USER_CONSTANTS.STUDENT) &&
        currentPrivilege !== USER_CONSTANTS.STUDENT
      ) {
        try {
          const response = await multiRolesUpdate({
            currentPrivilege: USER_CONSTANTS.STUDENT,
            userId: userDetails.id,
          });

          dispatch(
            setUserDetails({
              ...rest,
              privileges,
              currentPrivilege: USER_CONSTANTS.STUDENT,
            }),
          );
          dispatch(setOrgLogo(response?.data?.response?.logo as string));
          hasRunRef.current = false;
          return;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err) {
          toast.error("Failed to switch to student role.");
          setLoading(false);
          return;
        }
      }

      if (
        privileges.includes(USER_CONSTANTS.STUDENT) &&
        currentPrivilege === USER_CONSTANTS.STUDENT
      ) {
        try {
          if (quizError) {
            setLoading(false);
            return;
          }

          const organizationId = quizData?.response?.organizationId;
          const scope = quizData?.response?.scope;
          const status = quizData?.response?.quizStudentStatus;
          const isGre = quizData?.response?.quizType?.toUpperCase() === "GRE";

          const openGreWindow = (url: string) => {
            const width = screen.width;
            const height = screen.height;
            const features = `width=${width},height=${height},top=0,left=0,fullscreen=yes,toolbar=no,menubar=no,scrollbars=no,resizable=no,location=no,status=no`.replace(
              /\s+/g,
              "",
            );
            const fullUrl = `${window.location.origin}${url}`;
            const newWindow = window.open(fullUrl, "_blank", features);
            if (newWindow) {
              newWindow.focus();
            } else {
              alert("Please enable popups to continue.");
            }
          };

          if (scope === "GLOBAL") {
            if (status === "NEW") {
              if (isGre) {
                openGreWindow("/website-quiz/quiz-screen");
                navigate("/");
              } else {
                navigate("/website-quiz/instruction", {
                  state: {
                    quizId,
                    showJoinSuccess: true,
                    from: `/quiz/assign/${quizId}`,
                  },
                });
              }
            } else {
              toast.info("You have already participated in this quiz.");
              navigate("/website-quiz");
            }
            setLoading(false);
            return;
          }

          if (!organizationId) {
            throw new Error("No organization found for this quiz.");
          }

          dispatch(setOrganizationData({ organizationId }));

          const response = await updateStudentOrg({
            organizationId: organizationId,
          }).unwrap();
          dispatch(setOrgLogo(response?.response?.logo));
          if (status === "NEW" || status === "NOT_COMPLETED") {
            if (isGre) {
              openGreWindow("/activities/myQuiz/quiz-screen");
              navigate("/");
            } else {
              navigate("/activities/myQuiz/instruction", {
                state: {
                  quizId,
                  showJoinSuccess: true,
                  from: `/quiz/assign/${quizId}`,
                  quizLink: true,
                },
              });
            }
          } else {
            toast.info("You have already participated in this quiz.");
            navigate("/activities/myQuiz");
          }
        } catch (err) {
          const error = err as {
            data?: { response?: string };
            message?: string;
          };
          const errorMsg =
            error?.data?.response || "Failed to join quiz or set organization.";
          setErrorMessage(errorMsg);
          setShowErrorModal(true);
        }
      } else {
        setShowAccessModal(true);
      }

      setLoading(false);
    };

    if (!hasRunRef.current && !isFetching) {
      hasRunRef.current = true;
      handleJoinQuizFlow();
    }
  }, [
    quizId,
    isAuthenticated,
    userDetails,
    quizData,
    quizError,
    isFetching,
    dispatch,
    navigate,
    multiRolesUpdate,
    updateStudentOrg,
  ]);

  const isAlreadyJoined =
    errorMessage?.toLowerCase().includes("already joined") ||
    errorMessage?.toLowerCase().includes("already participated");

  const isOrgScope =
    quizData?.response?.scope === "ORGANIZATION" ||
    (quizError as { data?: { scope?: string; response?: { scope?: string } } })
      ?.data?.response?.scope === "ORGANIZATION" ||
    (quizError as { data?: { scope?: string; response?: { scope?: string } } })
      ?.data?.scope === "ORGANIZATION";

  const quizListPath = isOrgScope ? "/activities/myQuiz" : "/website-quiz";

  if (
    quizError &&
    isNetworkOrServerError(quizError) &&
    !showAccessModal &&
    !showErrorModal
  ) {
    return (
      <>
        <Toastify />
        <QuizLoadError
          title="Unable to open this quiz"
          message="The quiz could not be loaded. This can happen on a slow connection. Retry to continue joining."
          onRetry={() => {
            hasRunRef.current = false;
            setLoading(true);
            refetch();
          }}
          isRetrying={isFetching}
        />
      </>
    );
  }

  return (
    <>
      <Toastify />
      {loading && <CommonLoader />}

      {showAccessModal && (
        <Modal
          isOpen={showAccessModal}
          headerTitle="Access Denied"
          height={151}
          body={
            <div style={{ padding: "10px 0" }}>
              <p>Only students can join the quiz through this link.</p>
            </div>
          }
          primaryButtonText="Go to Home"
          onPrimaryButtonClick={() => {
            setShowAccessModal(false);
            navigate("/");
          }}
          showSecondaryButton={false}
          onClose={() => {
            setShowAccessModal(false);
            navigate("/");
          }}
        />
      )}

      {showErrorModal && (
        <Modal
          isOpen={showErrorModal}
          headerTitle={isAlreadyJoined ? "Already Joined" : "Error Joining Quiz"}
          height={160}
          body={
            <div style={{ padding: "10px 0" }}>
              <p>{errorMessage}</p>
            </div>
          }
          primaryButtonText={
            isAlreadyJoined ? "Go to My Quizzes" : "Go to Home"
          }
          onPrimaryButtonClick={() => {
            setShowErrorModal(false);
            navigate(isAlreadyJoined ? quizListPath : "/");
          }}
          showSecondaryButton={isAlreadyJoined}
          secondaryButtonText="Go to Home"
          onSecondaryButtonClick={() => {
            setShowErrorModal(false);
            navigate("/");
          }}
          onClose={() => {
            setShowErrorModal(false);
            navigate(isAlreadyJoined ? quizListPath : "/");
          }}
        />
      )}
    </>
  );
};

export default JoinQuizHandler;